import { Injectable } from '@angular/core';
import { OrderAddress } from '@api-ui-app/src/app/orders/lib/order-address';
import { AddressValidateRequest } from '@api-ui-app/src/app/shared/lib/address-validate-request';
import { ItemFileType } from '@api-ui-app/src/app/orders/lib/order-item';
import { EProductVariant } from '@gelato-api-ui/core/e-commerce/e-product-variant';
import { EProductTreeItem } from '@api-ui-app/src/app/product/product-add/types/e-product-tree-item';
import { OrderCreateItem, SaveOrderRequest } from '@api-ui-app/src/app/checkout/types/save-order-request';
import { getCustomTrimValuesMm } from '@gelato-api-ui/core/e-commerce/helpers/getCustomTrimValuesMm';
import { ITEM_DEFAULT_QUANTITY } from '@api-ui-app/src/app/checkout/constants/checkout.constants';
import { MetadataItem } from '@gelato-api-ui/core/metadata/types/metadata-item';
import * as R from 'ramda';
import { CheckoutDefaultDataService } from '@api-ui-app/src/app/checkout/services/checkout-default-data.service';
import {
  CheckoutQuoteProduct,
  CheckoutQuoteRequest,
  QuoteOrder,
  QuoteRecipient,
} from '@api-ui-app/src/app/checkout/types/checkout-quote-request';

@Injectable()
export class CheckoutDataMappingService {
  constructor(private readonly defaultDataService: CheckoutDefaultDataService) {}

  mapAddressToAddressValidateRequest(address: OrderAddress) {
    return address
      ? new AddressValidateRequest(
          address.firstName,
          address.lastName,
          address.companyName,
          address.addressLine1,
          address.addressLine2,
          address.city,
          address.state,
          address.postCode,
          address.country,
          address.email,
          address.phone,
          address.isBusiness,
          address.stateTaxId,
          address.registrationState,
          address.federalTaxId,
        )
      : null;
  }

  mapProductVariantsToCreateItems(
    productVariants: EProductVariant[],
    productsTree: EProductTreeItem[],
  ): OrderCreateItem[] {
    if (!productVariants?.length) {
      return [];
    }

    return R.map((loopProductVariant: EProductVariant): OrderCreateItem => {
      const customTrimMm = getCustomTrimValuesMm(loopProductVariant.customTrim);
      return {
        id: null,
        itemReferenceId:
          loopProductVariant.externalVariantId || this.defaultDataService.generateDefaultItemReferenceId(),
        itemReferenceName: this.getStoreName(loopProductVariant, productsTree),
        productUid: loopProductVariant.productUid,
        designId: loopProductVariant.designId,
        files: loopProductVariant.fileUrl ? [{ type: ItemFileType.default, url: loopProductVariant.fileUrl }] : null,
        quantity: ITEM_DEFAULT_QUANTITY,
        pageCount: loopProductVariant.pageCount || null,
        options: [],
        retailPriceInclVat: loopProductVariant.price || null,
        metadata: this.patchMetaData(loopProductVariant, productsTree),
        isIgnored: false,
        customTrim: customTrimMm ? { widthMm: customTrimMm.width, heightMm: customTrimMm.height } : null,
        storeProductId: null,
        storeProductVariantId: null,
      };
    }, productVariants);
  }

  mapOrderRequestToQuoteRequest(order: SaveOrderRequest): CheckoutQuoteRequest {
    return {
      ...this.mapOrderRequestToQuoteOrder(order),
      recipient: this.mapOrderRequestToQuoteRecipient(order),
      products: this.mapOrderRequestToQuoteProducts(order),
      allowMultipleQuotes: false,
    } as CheckoutQuoteRequest;
  }

  private getProductTree(productVariantId: string, productsTree: EProductTreeItem[]): EProductTreeItem {
    return productsTree.find(productTree => productTree.id === productVariantId);
  }

  private getStoreName(loopProductVariant: EProductVariant, productsTree: EProductTreeItem[]): string {
    const productTree = this.getProductTree(loopProductVariant.productId, productsTree);
    return productTree ? this.getProductTree(loopProductVariant.productId, productsTree).title : null;
  }

  private patchMetaData(loopProductVariant: EProductVariant, productsTree: EProductTreeItem[]): MetadataItem[] {
    const productTree = this.getProductTree(loopProductVariant.productId, productsTree);
    const metaData = [];
    if (productTree) {
      metaData.push({
        key: 'title',
        value: this.getProductTree(loopProductVariant.productId, productsTree).title,
      });
    }
    return metaData;
  }

  private mapOrderRequestToQuoteOrder({
    id,
    clientId,
    customerReferenceId,
    orderReferenceId,
    iossNumber,
    currency,
    retailCurrency,
    retailShippingPriceInclVat,
  }: SaveOrderRequest): QuoteOrder {
    return {
      id,
      clientId,
      customerReferenceId,
      orderReferenceId,
      iossNumber,
      currency,
      retailCurrency,
      retailShippingPriceInclVat,
    } as QuoteOrder;
  }

  private mapOrderRequestToQuoteRecipient(order: SaveOrderRequest): QuoteRecipient {
    return {
      country: order.shippingAddress.country,
      companyName: order.shippingAddress.companyName,
      firstName: order.shippingAddress.firstName,
      lastName: order.shippingAddress.lastName,
      addressLine1: order.shippingAddress.addressLine1,
      addressLine2: order.shippingAddress.addressLine2,
      city: order.shippingAddress.city,
      postCode: order.shippingAddress.postCode,
      state: order.shippingAddress.state,
      email: order.shippingAddress.email,
      phone: order.shippingAddress.phone,
    } as QuoteRecipient;
  }

  private mapOrderRequestToQuoteProducts(order: SaveOrderRequest): CheckoutQuoteProduct[] {
    return order.items
      .filter((item: OrderCreateItem): boolean => !item.isIgnored)
      .map((item: OrderCreateItem): CheckoutQuoteProduct => {
        const defaultFileUrl = item.files?.find(f => f.type === ItemFileType.default)?.url;
        const firstFileUrl = item.files?.length ? item.files[0].url : null;

        return {
          fileUrl: item.designId ? null : defaultFileUrl || firstFileUrl || null,
          designId: item.designId,
          itemReferenceId: item.itemReferenceId,
          productUid: item.productUid,
          quantity: item.quantity,
          pageCount: item.pageCount,
          options: item.options,
          customTrim: item.customTrim,
          retailPriceInclVat: item.retailPriceInclVat,
        };
      });
  }
}
